import React, { useEffect, useState, useCallback, useRef } from 'react';

const C = {
  bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22',
  text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', dim:'rgba(237,232,220,0.35)',
  gold:'#C9A84C', goldLt:'#E2C97E', red:'#ef4444', amber:'#f59e0b',
  green:'#22c55e', blue:'#3b82f6', purple:'#8b5cf6'
};

const authHdr = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('erue_auth_token') || ''}`
});

interface Room {
  id: string; title: string; description?: string;
  scope: string; status: string; groupTag?: string;
  categoryTags: string[]; scheduledAt: string;
  hostEmail: string; participantCount: number; messageCount: number;
  myClearance?: string; myIsPresent?: boolean;
  isPasswordProtected: boolean;
  socialShareLinks?: Record<string,string>;
  agendaItems?: any[];
}
interface Message {
  id: string; senderEmail: string; senderName: string;
  content: string; createdAt: string; integrityOk?: boolean;
  attachmentUrl?: string;
}
interface Group { tag: string; label: string; icon: string; }
interface Participant {
  email: string; displayName: string; clearance: string;
  isPresent: boolean; isMuted: boolean; groupTag?: string;
}
interface Props { currentUser: any; }
type HubView = 'lobby' | 'room' | 'create';

export default function CollaborationHub({ currentUser }: Props) {
  const [view,        setView]        = useState<HubView>('lobby');
  const [rooms,       setRooms]       = useState<Room[]>([]);
  const [myRooms,     setMyRooms]     = useState<Room[]>([]);
  const [groups,      setGroups]      = useState<Group[]>([]);
  const [activeRoom,  setActiveRoom]  = useState<Room | null>(null);
  const [roomDetail,  setRoomDetail]  = useState<any>(null);
  const [messages,    setMessages]    = useState<Message[]>([]);
  const [msgInput,    setMsgInput]    = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterScope, setFilterScope] = useState('');
  const [filterStatus,setFilterStatus]= useState('');
  const [loading,     setLoading]     = useState(false);
  const [sendingMsg,  setSendingMsg]  = useState(false);
  const [feedback,    setFeedback]    = useState('');
  const [copied,      setCopied]      = useState<string|null>(null);
  const [activeTab,   setActiveTab]   = useState<'chat'|'participants'|'agenda'|'audit'>('chat');
  const [auditLog,    setAuditLog]    = useState<any[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval>|null>(null);
  const msgEndRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({
    title:'', description:'', scope:'GROUP', groupTag:'LOC',
    scheduledAt: new Date(Date.now()+3600000).toISOString().slice(0,16),
    isPasswordProtected: false, password:'', maxParticipants:'',
    agendaText:''
  });

  const email = currentUser?.email || '';

  const loadGroups = useCallback(async () => {
    try {
      const r = await fetch('/api/collab/groups', { headers: authHdr() });
      const d = await r.json();
      if (d.success) setGroups(d.groups);
    } catch {}
  }, []);

  const loadRooms = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (filterGroup)  p.set('groupTag', filterGroup);
      if (filterScope)  p.set('scope', filterScope);
      if (filterStatus) p.set('status', filterStatus);
      const r = await fetch(`/api/collab/rooms?${p}`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) setRooms(d.rooms);
    } catch {}
  }, [filterGroup, filterScope, filterStatus]);

  const loadMyRooms = useCallback(async () => {
    try {
      const r = await fetch('/api/collab/my-rooms', { headers: authHdr() });
      const d = await r.json();
      if (d.success) setMyRooms(d.rooms);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (roomId: string) => {
    try {
      const r = await fetch(`/api/collab/rooms/${roomId}/messages?limit=100`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) {
        setMessages(d.messages);
        setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
      }
    } catch {}
  }, []);

  const loadRoomDetail = useCallback(async (roomId: string) => {
    try {
      const r = await fetch(`/api/collab/rooms/${roomId}`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) setRoomDetail(d.room);
    } catch {}
  }, []);

  const loadAudit = useCallback(async (roomId: string) => {
    try {
      const r = await fetch(`/api/collab/rooms/${roomId}/audit`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) setAuditLog(d.entries);
    } catch {}
  }, []);

  useEffect(() => { loadGroups(); loadRooms(); loadMyRooms(); }, []);
  useEffect(() => { if (view === 'lobby') { loadRooms(); loadMyRooms(); } }, [view]);
  useEffect(() => { loadRooms(); }, [filterGroup, filterScope, filterStatus]);

  useEffect(() => {
    if (activeRoom && view === 'room') {
      loadMessages(activeRoom.id);
      loadRoomDetail(activeRoom.id);
      pollRef.current = setInterval(() => {
        loadMessages(activeRoom.id);
        loadRoomDetail(activeRoom.id);
      }, 5000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
  }, [activeRoom, view]);

  const joinRoom = async (room: Room, password?: string) => {
    setLoading(true); setFeedback('');
    try {
      const r = await fetch(`/api/collab/rooms/${room.id}/join`, {
        method: 'POST', headers: authHdr(),
        body: JSON.stringify({ displayName: email, password })
      });
      const d = await r.json();
      if (!d.success) { setFeedback(d.error); setLoading(false); return; }
      setActiveRoom(room); setView('room'); setActiveTab('chat');
    } catch (e: any) { setFeedback(e.message); }
    finally { setLoading(false); }
  };

  const leaveRoom = async () => {
    if (!activeRoom) return;
    await fetch(`/api/collab/rooms/${activeRoom.id}/leave`, { method: 'POST', headers: authHdr() });
    if (pollRef.current) clearInterval(pollRef.current);
    setActiveRoom(null); setRoomDetail(null); setMessages([]); setView('lobby');
  };

  const sendMessage = async () => {
    if (!msgInput.trim() || !activeRoom || sendingMsg) return;
    setSendingMsg(true);
    try {
      const r = await fetch(`/api/collab/rooms/${activeRoom.id}/messages`, {
        method: 'POST', headers: authHdr(),
        body: JSON.stringify({ content: msgInput.trim(), displayName: email })
      });
      const d = await r.json();
      if (d.success) { setMsgInput(''); await loadMessages(activeRoom.id); }
      else setFeedback(d.error);
    } catch (e: any) { setFeedback(e.message); }
    finally { setSendingMsg(false); }
  };

  const changeStatus = async (status: string) => {
    if (!activeRoom) return;
    const r = await fetch(`/api/collab/rooms/${activeRoom.id}/status`, {
      method: 'PUT', headers: authHdr(), body: JSON.stringify({ status })
    });
    const d = await r.json();
    if (d.success) { setActiveRoom((prev: any) => prev ? { ...prev, status } : prev); await loadRoomDetail(activeRoom.id); }
    else setFeedback(d.error);
  };

  const deleteMessage = async (msgId: string) => {
    if (!activeRoom) return;
    await fetch(`/api/collab/rooms/${activeRoom.id}/messages/${msgId}`, { method: 'DELETE', headers: authHdr() });
    await loadMessages(activeRoom.id);
  };

  const createRoom = async () => {
    if (!form.title.trim()) return setFeedback('Title required');
    setLoading(true); setFeedback('');
    try {
      const agendaItems = form.agendaText.trim()
        ? form.agendaText.split('\n').filter(Boolean).map((t, i) => ({ order: i+1, title: t.trim() }))
        : [];
      const r = await fetch('/api/collab/rooms', {
        method: 'POST', headers: authHdr(),
        body: JSON.stringify({
          title: form.title, description: form.description,
          scope: form.scope, groupTag: form.groupTag,
          scheduledAt: new Date(form.scheduledAt).toISOString(),
          isPasswordProtected: form.isPasswordProtected,
          password: form.password,
          maxParticipants: form.maxParticipants ? parseInt(form.maxParticipants) : null,
          agendaItems
        })
      });
      const d = await r.json();
      if (d.success) { await loadRooms(); await loadMyRooms(); setView('lobby'); }
      else setFeedback(d.error);
    } catch (e: any) { setFeedback(e.message); }
    finally { setLoading(false); }
  };

  const copyLink = async (text: string, key: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 2000); }
    catch {}
  };

  const scopeColor  = (s: string) => ({ PRIVATE: C.purple, GROUP: C.gold, CATEGORY: C.blue, ALL_HANDS: C.green }[s] || C.muted);
  const statusColor = (s: string) => ({ SCHEDULED: C.amber, LIVE: C.green, ENDED: C.dim, CANCELLED: C.red }[s] || C.muted);
  const groupIcon   = (tag?: string) => groups.find(g => g.tag === tag)?.icon || '🏠';
  const isHost      = activeRoom?.hostEmail === email;
  const myClearance = roomDetail?.participants?.find((p: any) => p.email === email)?.clearance || '';
  const canManage   = isHost || myClearance === 'HOST' || myClearance === 'ADMIN';

  // ── ROOM VIEW ───────────────────────────────────────────────────────────────
  if (view === 'room' && activeRoom) return (
    <div style={{ display:'flex', flexDirection:'column', height:'100vh', background:C.bg, color:C.text, fontFamily:'system-ui,sans-serif' }}>

      {/* Header */}
      <div style={{ padding:'10px 16px', background:C.surface, borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <button onClick={leaveRoom} style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, cursor:'pointer', fontSize:12 }}>← Leave</button>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.goldLt }}>{groupIcon(activeRoom.groupTag)} {activeRoom.title}</div>
          <div style={{ fontSize:11, color:C.muted, display:'flex', gap:8, flexWrap:'wrap', marginTop:2 }}>
            <span style={{ color:scopeColor(activeRoom.scope) }}>{activeRoom.scope}</span>
            <span style={{ color:statusColor(activeRoom.status) }}>● {activeRoom.status}</span>
            {activeRoom.groupTag && <span>{activeRoom.groupTag}</span>}
            <span>👥 {roomDetail?.participants?.filter((p:any)=>p.isPresent).length||0} live</span>
            {myClearance && <span style={{ color:C.gold }}>You: {myClearance}</span>}
          </div>
        </div>

        {/* Host controls */}
        {canManage && activeRoom.status === 'SCHEDULED' && (
          <button onClick={() => changeStatus('LIVE')} style={{ padding:'5px 12px', borderRadius:6, border:'none', background:C.green, color:'#000', fontWeight:700, cursor:'pointer', fontSize:12 }}>▶ Start</button>
        )}
        {canManage && activeRoom.status === 'LIVE' && (
          <button onClick={() => changeStatus('ENDED')} style={{ padding:'5px 12px', borderRadius:6, border:'none', background:C.red, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>⏹ End</button>
        )}

        {/* Social share */}
        {activeRoom.socialShareLinks && (
          <div style={{ display:'flex', gap:6 }}>
            {activeRoom.socialShareLinks.whatsapp && (
              <a href={activeRoom.socialShareLinks.whatsapp} target="_blank" rel="noopener noreferrer"
                style={{ padding:'4px 10px', borderRadius:6, background:'#25D366', color:'#fff', fontSize:11, fontWeight:700, textDecoration:'none' }}>WA</a>
            )}
            {activeRoom.socialShareLinks.telegram && (
              <a href={activeRoom.socialShareLinks.telegram} target="_blank" rel="noopener noreferrer"
                style={{ padding:'4px 10px', borderRadius:6, background:'#229ED9', color:'#fff', fontSize:11, fontWeight:700, textDecoration:'none' }}>TG</a>
            )}
            <button onClick={() => copyLink(activeRoom.socialShareLinks!.direct, 'rl')}
              style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${C.border}`, background: copied==='rl'?C.green:'transparent', color: copied==='rl'?'#fff':C.muted, cursor:'pointer', fontSize:11 }}>
              {copied==='rl'?'✅':'🔗'}
            </button>
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, background:C.surface }}>
        {(['chat','participants','agenda','audit'] as const).map(tab => (
          <button key={tab} onClick={() => { setActiveTab(tab); if(tab==='audit'&&activeRoom) loadAudit(activeRoom.id); }}
            style={{ padding:'8px 16px', border:'none', borderBottom: activeTab===tab?`2px solid ${C.gold}`:'2px solid transparent',
                     background:'transparent', color: activeTab===tab?C.gold:C.muted, cursor:'pointer', fontSize:12, fontWeight:600, textTransform:'capitalize' }}>
            {tab==='chat'?'💬 Chat':tab==='participants'?'👥 People':tab==='agenda'?'📋 Agenda':'🔍 Audit'}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex:1, overflowY:'auto', padding:'16px' }}>

        {/* CHAT */}
        {activeTab === 'chat' && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {messages.length === 0 && (
              <div style={{ textAlign:'center', color:C.dim, marginTop:40, fontSize:14 }}>No messages yet. Start the conversation.</div>
            )}
            {messages.map(m => {
              const isMe = m.senderEmail === email;
              return (
                <div key={m.id} style={{ display:'flex', flexDirection:'column', alignItems: isMe?'flex-end':'flex-start' }}>
                  {!isMe && <div style={{ fontSize:11, color:C.gold, marginBottom:2, fontWeight:600 }}>{m.senderName}</div>}
                  <div style={{ maxWidth:'72%', padding:'8px 12px',
                    borderRadius: isMe?'12px 12px 2px 12px':'12px 12px 12px 2px',
                    background: isMe?'rgba(201,168,76,0.15)':C.card,
                    border:`1px solid ${isMe?C.gold:C.border}`, fontSize:13, lineHeight:1.6 }}>
                    {m.content}
                    {m.attachmentUrl && <a href={m.attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ display:'block', marginTop:4, fontSize:11, color:C.blue }}>📎 Attachment</a>}
                  </div>
                  <div style={{ fontSize:10, color:C.dim, marginTop:2, display:'flex', gap:6, alignItems:'center' }}>
                    <span>{new Date(m.createdAt).toLocaleTimeString()}</span>
                    {m.integrityOk === false && <span style={{ color:C.red }}>⚠️ integrity</span>}
                    {(isMe || canManage) && m.content !== '[deleted]' && (
                      <button onClick={() => deleteMessage(m.id)} style={{ border:'none', background:'transparent', color:C.dim, cursor:'pointer', fontSize:10, padding:'0 4px' }}>✕</button>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={msgEndRef} />
          </div>
        )}

        {/* PARTICIPANTS */}
        {activeTab === 'participants' && (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ color:C.muted, fontSize:12, marginBottom:8 }}>
              {roomDetail?.participants?.filter((p:any)=>p.isPresent).length||0} live · {roomDetail?.participants?.length||0} total
            </div>
            {(roomDetail?.participants || []).map((p: Participant) => (
              <div key={p.email} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:C.card, borderRadius:8, border:`1px solid ${C.border}` }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background: p.isPresent?C.green:C.dim }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, color:C.text }}>{p.displayName}</div>
                  <div style={{ fontSize:11, color:C.muted }}>{p.email} {p.groupTag ? `· ${p.groupTag}`:''}</div>
                </div>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:'rgba(255,255,255,0.05)',
                    color: p.clearance==='HOST'?C.gold:p.clearance==='ADMIN'?C.amber:C.muted }}>{p.clearance}</span>
                  {p.isMuted && <span style={{ fontSize:11, color:C.red }}>🔇</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* AGENDA */}
        {activeTab === 'agenda' && (
          <div>
            {(!activeRoom.agendaItems || activeRoom.agendaItems.length === 0) ? (
              <div style={{ color:C.dim, textAlign:'center', marginTop:40 }}>No agenda items set for this meeting.</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {activeRoom.agendaItems.map((item: any, i: number) => (
                  <div key={i} style={{ padding:'10px 14px', background:C.card, borderRadius:8, border:`1px solid ${C.border}`, display:'flex', gap:12 }}>
                    <div style={{ color:C.gold, fontWeight:700, fontSize:14, minWidth:24 }}>{item.order||i+1}.</div>
                    <div>
                      <div style={{ color:C.text, fontSize:13 }}>{item.title}</div>
                      {item.durationMins && <div style={{ color:C.muted, fontSize:11, marginTop:2 }}>⏱ {item.durationMins} min{item.presenter?` · ${item.presenter}`:''}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AUDIT */}
        {activeTab === 'audit' && (
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {!canManage ? (
              <div style={{ color:C.dim, textAlign:'center', marginTop:40 }}>Audit log is restricted to host/admin.</div>
            ) : auditLog.length === 0 ? (
              <div style={{ color:C.dim, textAlign:'center', marginTop:40 }}>No audit entries yet.</div>
            ) : auditLog.map((e: any) => (
              <div key={e.id} style={{ padding:'8px 12px', background:C.card, borderRadius:6, border:`1px solid ${C.border}`, display:'flex', gap:10 }}>
                <div style={{ color:C.gold, fontSize:11, minWidth:120 }}>{new Date(e.createdAt).toLocaleString()}</div>
                <div style={{ flex:1 }}>
                  <span style={{ color:C.amber, fontSize:12, fontWeight:700 }}>{e.action}</span>
                  {e.detail && <span style={{ color:C.muted, fontSize:11, marginLeft:8 }}>{e.detail}</span>}
                </div>
                <div style={{ color:C.dim, fontSize:11 }}>{e.actor}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message input — chat tab only */}
      {activeTab === 'chat' && (
        <>
          {feedback && <div style={{ padding:'6px 16px', background:'rgba(239,68,68,0.1)', color:C.red, fontSize:12 }}>{feedback}</div>}
          <div style={{ padding:'10px 16px', background:C.surface, borderTop:`1px solid ${C.border}`, display:'flex', gap:8 }}>
            <input value={msgInput} onChange={e => setMsgInput(e.target.value)}
              onKeyDown={e => e.key==='Enter' && !e.shiftKey && sendMessage()}
              placeholder="Message... (Enter to send)"
              style={{ flex:1, background:C.card, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, padding:'9px 14px', fontSize:13 }} />
            <button onClick={sendMessage} disabled={sendingMsg || !msgInput.trim()}
              style={{ padding:'9px 20px', borderRadius:8, border:'none', background:sendingMsg?C.dim:C.gold, color:C.bg, fontWeight:700, cursor:sendingMsg?'not-allowed':'pointer', fontSize:13 }}>
              {sendingMsg?'…':'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // ── CREATE VIEW ─────────────────────────────────────────────────────────────
  if (view === 'create') return (
    <div style={{ padding:24, background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'system-ui,sans-serif', maxWidth:640, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <button onClick={() => setView('lobby')} style={{ padding:'6px 14px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, cursor:'pointer', fontSize:13 }}>← Back</button>
        <div style={{ color:C.gold, fontWeight:700, fontSize:18 }}>Create Meeting Room</div>
      </div>
      {feedback && <div style={{ padding:'10px 14px', borderRadius:6, marginBottom:16, background:'rgba(239,68,68,0.1)', color:C.red, fontSize:13 }}>{feedback}</div>}

      {/* Scope */}
      <div style={{ marginBottom:16 }}>
        <label style={{ display:'block', fontSize:12, color:C.muted, marginBottom:6 }}>Meeting Scope</label>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {[{v:'PRIVATE',l:'🔒 Private',d:'Invite only'},{v:'GROUP',l:'👥 Group',d:'One committee'},{v:'CATEGORY',l:'🗂️ Category',d:'Multi-group'},{v:'ALL_HANDS',l:'🤝 All Hands',d:'Everyone'}].map(s => (
            <button key={s.v} onClick={() => setForm(p=>({...p,scope:s.v}))}
              style={{ padding:'8px 14px', borderRadius:8, cursor:'pointer', border:`1px solid ${form.scope===s.v?C.gold:C.border}`, background:form.scope===s.v?'rgba(201,168,76,0.12)':C.card, color:form.scope===s.v?C.gold:C.muted, fontSize:12 }}>
              <div style={{ fontWeight:700 }}>{s.l}</div>
              <div style={{ fontSize:10, opacity:.7 }}>{s.d}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Group Tag */}
      {(form.scope==='GROUP'||form.scope==='PRIVATE') && (
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', fontSize:12, color:C.muted, marginBottom:6 }}>Committee / Group</label>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {groups.map(g => (
              <button key={g.tag} onClick={() => setForm(p=>({...p,groupTag:g.tag}))}
                style={{ padding:'6px 12px', borderRadius:6, cursor:'pointer', fontSize:12, border:`1px solid ${form.groupTag===g.tag?C.gold:C.border}`, background:form.groupTag===g.tag?'rgba(201,168,76,0.12)':C.card, color:form.groupTag===g.tag?C.gold:C.muted }}>
                {g.icon} {g.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fields */}
      {[
        {label:'Room Title *', key:'title', type:'text', placeholder:'e.g. LOC Finance Review Q2'},
        {label:'Description', key:'description', type:'text', placeholder:'Optional overview'},
        {label:'Scheduled Date/Time *', key:'scheduledAt', type:'datetime-local', placeholder:''},
        {label:'Max Participants', key:'maxParticipants', type:'number', placeholder:'Blank = unlimited'},
      ].map(f => (
        <div key={f.key} style={{ marginBottom:14 }}>
          <label style={{ display:'block', fontSize:12, color:C.muted, marginBottom:4 }}>{f.label}</label>
          <input type={f.type} value={(form as any)[f.key]} placeholder={f.placeholder}
            onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))}
            style={{ width:'100%', background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'9px 12px', fontSize:13, boxSizing:'border-box' }} />
        </div>
      ))}

      {/* Agenda */}
      <div style={{ marginBottom:14 }}>
        <label style={{ display:'block', fontSize:12, color:C.muted, marginBottom:4 }}>Agenda Items (one per line)</label>
        <textarea value={form.agendaText} onChange={e => setForm(p=>({...p,agendaText:e.target.value}))}
          placeholder={"Opening remarks\nFinance update\nAOB"}
          rows={4}
          style={{ width:'100%', background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'9px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box' }} />
      </div>

      {/* Password */}
      <div style={{ marginBottom:16 }}>
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:C.muted }}>
          <input type="checkbox" checked={form.isPasswordProtected} onChange={e => setForm(p=>({...p,isPasswordProtected:e.target.checked}))} />
          Password-protect this room
        </label>
        {form.isPasswordProtected && (
          <input type="password" value={form.password} placeholder="Room password"
            onChange={e => setForm(p=>({...p,password:e.target.value}))}
            style={{ marginTop:8, width:'100%', background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'9px 12px', fontSize:13, boxSizing:'border-box' }} />
        )}
      </div>

      <button onClick={createRoom} disabled={loading}
        style={{ width:'100%', padding:'12px', borderRadius:8, border:'none', background:loading?C.dim:C.gold, color:C.bg, fontWeight:700, fontSize:15, cursor:loading?'not-allowed':'pointer' }}>
        {loading?'Creating…':'🚀 Create Room'}
      </button>
    </div>
  );

  // ── LOBBY VIEW ──────────────────────────────────────────────────────────────
  const liveRooms = myRooms.filter(r => r.status === 'LIVE');

  return (
    <div style={{ padding:24, background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'system-ui,sans-serif' }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ color:C.gold, fontWeight:700, fontSize:20 }}>🤝 Collaboration Hub</div>
          <div style={{ color:C.muted, fontSize:12, marginTop:2 }}>ERUE Festival Suite — Secure Committee Meeting Rooms</div>
        </div>
        <button onClick={() => { setFeedback(''); setForm({title:'',description:'',scope:'GROUP',groupTag:'LOC',scheduledAt:new Date(Date.now()+3600000).toISOString().slice(0,16),isPasswordProtected:false,password:'',maxParticipants:'',agendaText:''}); setView('create'); }}
          style={{ padding:'10px 20px', borderRadius:8, border:'none', background:C.gold, color:C.bg, fontWeight:700, cursor:'pointer', fontSize:13 }}>
          + New Room
        </button>
      </div>

      {feedback && <div style={{ padding:'10px 14px', borderRadius:6, marginBottom:16, background:'rgba(239,68,68,0.1)', color:C.red, fontSize:13 }}>{feedback}</div>}

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
        {[
          { val:filterScope, set:setFilterScope, label:'All Scopes', opts:[{v:'PRIVATE',l:'🔒 Private'},{v:'GROUP',l:'👥 Group'},{v:'CATEGORY',l:'🗂️ Category'},{v:'ALL_HANDS',l:'🤝 All Hands'}] },
          { val:filterStatus, set:setFilterStatus, label:'All Status', opts:[{v:'SCHEDULED',l:'⏰ Scheduled'},{v:'LIVE',l:'🟢 Live'},{v:'ENDED',l:'⬜ Ended'},{v:'CANCELLED',l:'❌ Cancelled'}] },
        ].map((f,i) => (
          <select key={i} value={f.val} onChange={e => f.set(e.target.value)}
            style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'7px 12px', fontSize:12 }}>
            <option value="">{f.label}</option>
            {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        ))}
        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}
          style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'7px 12px', fontSize:12 }}>
          <option value="">All Groups</option>
          {groups.map(g => <option key={g.tag} value={g.tag}>{g.icon} {g.label}</option>)}
        </select>
        <button onClick={() => { loadRooms(); loadMyRooms(); }}
          style={{ padding:'7px 14px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, cursor:'pointer', fontSize:12 }}>↻ Refresh</button>
      </div>

      {/* My Live Rooms */}
      {liveRooms.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <div style={{ color:C.green, fontWeight:700, fontSize:13, marginBottom:10 }}>🟢 LIVE — Your Active Rooms</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {liveRooms.map(room => <RoomCard key={room.id} room={room} onJoin={joinRoom} copyLink={copyLink} copied={copied} scopeColor={scopeColor} statusColor={statusColor} groupIcon={groupIcon} isHighlighted />)}
          </div>
        </div>
      )}

      {/* All Rooms */}
      <div>
        <div style={{ color:C.gold, fontWeight:700, fontSize:13, marginBottom:10 }}>All Visible Rooms ({rooms.length})</div>
        {rooms.length === 0 ? (
          <div style={{ color:C.dim, textAlign:'center', padding:'48px 0', fontSize:14 }}>No rooms found. Create one to get started.</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {rooms.map(room => <RoomCard key={room.id} room={room} onJoin={joinRoom} copyLink={copyLink} copied={copied} scopeColor={scopeColor} statusColor={statusColor} groupIcon={groupIcon} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ── RoomCard ──────────────────────────────────────────────────────────────────
function RoomCard({ room, onJoin, copyLink, copied, scopeColor, statusColor, groupIcon, isHighlighted }: any) {
  const C2 = { card:'#121E16', border:'#1C2E22', text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', dim:'rgba(237,232,220,0.35)', gold:'#C9A84C', green:'#22c55e', amber:'#f59e0b', red:'#ef4444' };
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState('');

  return (
    <div style={{ background:isHighlighted?'rgba(34,197,94,0.06)':C2.card, border:`1px solid ${isHighlighted?C2.green:C2.border}`, borderRadius:10, padding:'14px 16px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C2.text, marginBottom:4 }}>{groupIcon(room.groupTag)} {room.title}</div>
          {room.description && <div style={{ color:C2.muted, fontSize:12, marginBottom:6, lineHeight:1.5 }}>{room.description}</div>}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', fontSize:11 }}>
            <span style={{ padding:'2px 8px', borderRadius:10, background:'rgba(255,255,255,0.05)', color:scopeColor(room.scope) }}>{room.scope}</span>
            <span style={{ color:statusColor(room.status) }}>● {room.status}</span>
            {room.groupTag && <span style={{ color:C2.muted }}>{room.groupTag}</span>}
            <span style={{ color:C2.muted }}>👥 {room.participantCount} · 💬 {room.messageCount}</span>
            <span style={{ color:C2.muted }}>📅 {new Date(room.scheduledAt).toLocaleString()}</span>
            {room.isPasswordProtected && <span style={{ color:C2.amber }}>🔒</span>}
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end', flexShrink:0 }}>
          <button onClick={() => room.isPasswordProtected ? setShowPw(true) : onJoin(room)}
            disabled={room.status==='ENDED'||room.status==='CANCELLED'}
            style={{ padding:'7px 16px', borderRadius:6, border:'none', fontWeight:700, fontSize:12, cursor:room.status==='ENDED'||room.status==='CANCELLED'?'not-allowed':'pointer',
              background:room.status==='LIVE'?C2.green:room.status==='SCHEDULED'?C2.gold:C2.dim,
              color:room.status==='ENDED'||room.status==='CANCELLED'?'#666':'#0A0A0A' }}>
            {room.status==='LIVE'?'▶ Join Live':room.status==='SCHEDULED'?'Enter':room.status}
          </button>
          {room.socialShareLinks?.direct && (
            <button onClick={() => copyLink(room.socialShareLinks.direct, `r-${room.id}`)}
              style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${C2.border}`, background:copied===`r-${room.id}`?C2.green:'transparent', color:copied===`r-${room.id}`?'#fff':C2.muted, cursor:'pointer', fontSize:11 }}>
              {copied===`r-${room.id}`?'✅':'🔗 Share'}
            </button>
          )}
        </div>
      </div>
      {showPw && (
        <div style={{ marginTop:10, display:'flex', gap:8 }}>
          <input type="password" value={pw} placeholder="Room password" onChange={e => setPw(e.target.value)}
            style={{ flex:1, background:'#0E1812', border:`1px solid ${C2.border}`, borderRadius:6, color:C2.text, padding:'7px 10px', fontSize:12 }} />
          <button onClick={() => { onJoin(room, pw); setShowPw(false); setPw(''); }}
            style={{ padding:'7px 14px', borderRadius:6, border:'none', background:C2.gold, color:'#000', fontWeight:700, cursor:'pointer', fontSize:12 }}>Enter</button>
          <button onClick={() => setShowPw(false)}
            style={{ padding:'7px 10px', borderRadius:6, border:`1px solid ${C2.border}`, background:'transparent', color:C2.muted, cursor:'pointer', fontSize:12 }}>✕</button>
        </div>
      )}
    </div>
  );
}
